import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, ArrowUpRight, Tag } from "@phosphor-icons/react";
import DOMPurify from "dompurify";
import { api, formatEventDate } from "../lib/api";
import Seo from "../components/Seo";

const NewsDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/news/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        // load related: same category, excluding current
        api.get("/news", { params: { category: data.category, limit: 6 } })
          .then(({ data: list }) => setRelated(list.filter((x) => x.id !== data.id).slice(0, 3)));
      })
      .catch((e) => setError(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (error) return <div className="p-12 text-white" data-testid="news-error">Article not found.</div>;
  if (!article) return <div className="p-12 text-white/40 font-mono-tech" data-testid="news-loading">/ loading...</div>;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.hero_image ? [article.hero_image] : undefined,
    "datePublished": article.published_at,
    "dateModified": article.updated_at || article.published_at,
    "author": [{ "@type": "Person", "name": article.author }],
    "publisher": {
      "@type": "Organization",
      "name": "FintechOmni",
      "logo": { "@type": "ImageObject", "url": "https://fintechomni.com/og-default.jpg" },
    },
    "mainEntityOfPage": `https://fintechomni.com/news/${article.slug}`,
    "articleSection": article.category,
    "keywords": (article.tags || []).join(", "),
  };

  return (
    <main className="grain" data-testid={`news-detail-${article.slug}`}>
      <Seo
        path={`/news/${article.slug}`}
        title={article.title}
        description={article.excerpt}
        image={article.hero_image}
        type="article"
        publishedAt={article.published_at}
        updatedAt={article.updated_at}
        author={article.author}
        keywords={(article.tags || []).join(", ")}
        jsonLd={articleJsonLd}
      />
      {/* HERO */}
      <section className="relative border-b border-cyber-line">
        {article.hero_image && (
          <div className="absolute inset-0">
            <img src={article.hero_image} alt={article.title} className="w-full h-full object-cover opacity-25" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <div className="absolute inset-0 bg-gradient-to-b from-cyber-black/40 via-cyber-black/80 to-cyber-black" />
          </div>
        )}
        <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 pt-12 pb-20">
          <Link to="/news" className="font-mono-tech text-xs text-white tracking-[0.2em] uppercase flex items-center gap-2"><ArrowLeft size={12} /> All news</Link>

          <div className="mt-10 flex items-center gap-3 flex-wrap">
            <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-cyber-green border border-cyber-green px-2 py-1">{article.category}</span>
            {article.featured && <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-cyber-green/80">/ featured</span>}
            <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/50 flex items-center gap-1"><Clock size={11} /> {article.read_time_min} min read</span>
          </div>

          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[0.95]">
            {article.title}
          </h1>
          <p className="mt-6 text-white/80 text-xl max-w-3xl leading-relaxed">{article.excerpt}</p>

          <div className="mt-10 flex items-center gap-4 flex-wrap font-mono-tech text-xs text-white/60 uppercase tracking-[0.2em]">
            <span>By {article.author}</span>
            <span className="text-white/30">·</span>
            <span>{article.source}</span>
            <span className="text-white/30">·</span>
            <span>{formatEventDate(article.published_at)}</span>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[760px] mx-auto px-5 sm:px-8 py-20">
          {article.body?.trim().startsWith("<") ? (
            <div
              className="article-body max-w-none"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(article.body, {
                  ALLOWED_TAGS: ["p", "h2", "h3", "h4", "strong", "em", "u", "a", "ul", "ol", "li", "blockquote", "figure", "figcaption", "div", "span", "br", "hr", "aside", "small"],
                  ALLOWED_ATTR: ["href", "target", "rel", "class"],
                }),
              }}
            />
          ) : (
            <div className="prose prose-invert max-w-none">
              {article.body.split("\n\n").map((para, i) => (
                <p key={i} className="text-white/85 text-lg leading-[1.8] mb-6 whitespace-pre-line">{para}</p>
              ))}
            </div>
          )}

          {article.tags && article.tags.length > 0 && (
            <div className="mt-12 flex items-center gap-3 flex-wrap border-t border-cyber-line pt-8">
              <Tag size={14} className="text-cyber-green" />
              {article.tags.map((t) => (
                <span key={t} className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-white/70 border border-cyber-line px-3 py-1">{t}</span>
              ))}
            </div>
          )}

          {article.source_url && (
            <a href={article.source_url} target="_blank" rel="noreferrer" className="btn-ghost-neon mt-10" data-testid="news-source-link">
              Read original source <ArrowUpRight size={14} weight="bold" />
            </a>
          )}
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-cyber-black">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
            <div className="term-label mb-3">/ keep reading</div>
            <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl mb-8">More from {article.category}.</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {related.map((a) => (
                <Link key={a.id} to={`/news/${a.slug}`} className="group hard-border bg-cyber-surface overflow-hidden">
                  {a.hero_image && <div className="aspect-[16/10] overflow-hidden bg-cyber-black"><img src={a.hero_image} alt={a.title} className="w-full h-full object-cover img-grey" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} /></div>}
                  <div className="p-5">
                    <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-cyber-green">{a.category}</div>
                    <div className="font-display font-black text-xl mt-2 leading-tight line-clamp-2 group-hover:text-white/90 transition-colors">{a.title}</div>
                    <div className="font-mono-tech text-[10px] text-white/40 uppercase tracking-[0.2em] mt-3">{formatEventDate(a.published_at)} · {a.read_time_min}m</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default NewsDetail;
